import { CONSTELLATION_MAX_STARS as MAX_LIT } from '@aiai/shared';
import { seededUnit } from './layout';

const NOUNS = [
  'Cartographer', 'Heron', 'Lantern', 'Anvil', 'Kestrel', 'Compass', 'Weaver', 'Beacon',
  'Harrow', 'Loom', 'Orchard', 'Mariner', 'Sextant', 'Fox', 'Ferryman', 'Quill',
  'Bellows', 'Lighthouse', 'Osprey', 'Plough', 'Atlas', 'Archer', 'Tinker', 'Sparrow',
  'Pilot', 'Forge', 'Scribe', 'Lynx', 'Chisel', 'Gardener', 'Vessel', 'Comet',
  'Owl', 'Hammer', 'Kite', 'Wren', 'Stag', 'Mason', 'Ranger', 'Spindle',
  'Courier', 'Hourglass', 'Ladder', 'Tern', 'Keystone', 'Drum', 'Raven', 'Sail',
] as const;

const POINTS = 37;
const BOUND = 110;
/** Poisson-disc spacing between any two stars; relaxed in steps only if a sky can't fit 37. */
const SPACING = 26;
const MIN_SPACING = 22;
const ATTEMPTS = 3000;

/**
 * seededUnit with its FNV output re-mixed (murmur3 fmix32): FNV of consecutive keys is
 * correlated, which lines raw draws up in streaks — fine for one draw, not for a scatter.
 */
function unit(seed: string, n: number): number {
  let h = Math.floor(seededUnit(seed, n) * 0x100000000) >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 0x100000000;
}

export interface ConstellationFigure {
  /** In growth order: point 0 is the head (the newest shipped issue), at the origin. */
  points: Array<{ x: number; y: number }>;
  /** `edges[k - 1] = [parent, k]`: point k attached to an earlier point. A tree of 36 edges. */
  edges: Array<[number, number]>;
}

export function constellationName(projectId: string): string {
  return 'The ' + NOUNS[Math.floor(seededUnit(projectId, 900) * NOUNS.length)];
}

/** The visible label: the name and the full shipped total (it says the total past 36). */
export function constellationLabelText(projectId: string, shippedCount: number): string {
  return `${constellationName(projectId)} · ${shippedCount} shipped`;
}
/** The figure is drawn at this scale around its anchor (±110 → ±55 world units). */
export const CONSTELLATION_SCALE = 0.5;
/** The label's screen font size (px). */
export const CONSTELLATION_LABEL_PX = 10;
const LABEL_GAP = 6;

/**
 * Where the name label hangs, relative to the anchor (world units, already scaled): centred
 * under the stars actually drawn (lit + hinted), just below the lowest, so it never crosses one.
 */
export function constellationLabelAnchor(projectId: string, shippedCount: number): { x: number; y: number } {
  const figure = constellationFigure(projectId);
  const { lit, hints } = constellationShown(figure, litCount(shippedCount));
  const shown = [...lit, ...hints].map((i) => figure.points[i]);
  const xs = shown.map((p) => p.x * CONSTELLATION_SCALE);
  const maxY = Math.max(...shown.map((p) => p.y * CONSTELLATION_SCALE));
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: maxY + LABEL_GAP };
}

/**
 * A seeded, scattered star figure: 37 points inside ±110, at least SPACING apart (dart throwing
 * with seededUnit, a bounded number of attempts), then ordered by growth from the head — each
 * next star is the unvisited one nearest the figure so far (Prim's MST), attached by the edge
 * that reached it. The result branches like a real constellation instead of running in chains.
 */
export function constellationFigure(projectId: string): ConstellationFigure {
  const pts: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
  let n = 0;
  for (let gap = SPACING; pts.length < POINTS && gap >= MIN_SPACING; gap -= 2) {
    for (let a = 0; a < ATTEMPTS && pts.length < POINTS; a++, n++) {
      const x = (unit(projectId, 2000 + n * 2) * 2 - 1) * BOUND;
      const y = (unit(projectId, 2001 + n * 2) * 2 - 1) * BOUND;
      if (pts.every((p) => Math.hypot(p.x - x, p.y - y) >= gap)) pts.push({ x, y });
    }
  }
  // Prim's growth from the head; a seeded rank breaks exact ties.
  const rank = pts.map((_, i) => unit(projectId, 9000 + i));
  const inTree = pts.map((_, i) => i === 0);
  const best = pts.map((p) => Math.hypot(p.x, p.y));
  const from = pts.map(() => 0);
  const order = [0];
  const parentOf = new Map<number, number>();
  while (order.length < pts.length) {
    let next = -1;
    for (let j = 0; j < pts.length; j++) {
      if (inTree[j]) continue;
      if (next < 0 || best[j] < best[next] - 1e-9 || (Math.abs(best[j] - best[next]) <= 1e-9 && rank[j] < rank[next])) next = j;
    }
    inTree[next] = true;
    parentOf.set(next, from[next]);
    order.push(next);
    for (let j = 0; j < pts.length; j++) {
      if (inTree[j]) continue;
      const d = Math.hypot(pts[j].x - pts[next].x, pts[j].y - pts[next].y);
      if (d < best[j]) {
        best[j] = d;
        from[j] = next;
      }
    }
  }
  const pos = new Map(order.map((orig, i) => [orig, i]));
  return {
    points: order.map((i) => pts[i]),
    edges: order.slice(1).map((orig, k) => [pos.get(parentOf.get(orig)!)!, k + 1] as [number, number]),
  };
}


/**
 * Lit = the head plus the next lit−1 stars in growth order; hints = up to 3 of the next unlit stars
 * that hang off a lit one. A full figure (lit ≥ MAX_LIT) shows no hints: none of them could ever light.
 */
export function constellationShown(figure: ConstellationFigure, lit: number): { lit: number[]; hints: number[] } {
  if (lit <= 0) return { lit: [], hints: [] };
  const n = Math.min(lit, figure.points.length);
  const hints: number[] = [];
  for (let k = n; k < figure.points.length && n < MAX_LIT && hints.length < 3; k++) {
    if (figure.edges[k - 1][0] < n) hints.push(k);
  }
  return { lit: Array.from({ length: n }, (_, i) => i), hints };
}

export function litCount(shipped: number): number {
  return Math.min(MAX_LIT, Math.max(0, shipped));
}
